import { Component, inject, ViewContainerRef, effect, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { SynDialogComponent } from '@synergy-design-system/angular';
import { ModalService } from '@core/services/modal.service';

/**
 * ModalHostComponent
 *
 * Persistent global popup host driven by ModalService signals. Renders an
 * arbitrary standalone component inside a centered `syn-dialog`.
 * Place once in app.component.html outside the router-outlet.
 */
@Component({
  selector: 'app-modal-host',
  standalone: true,
  imports: [SynDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <syn-dialog
      [label]="svc.title()"
      [open]="svc.isOpen()"
      [style.--width]="svc.width()"
      (syn-request-close)="onRequestClose($event)"
    >
      <ng-container #outlet></ng-container>
    </syn-dialog>
  `,
})
export class ModalHostComponent {
  protected readonly svc = inject(ModalService);

  private readonly outlet = viewChild.required('outlet', { read: ViewContainerRef });

  constructor() {
    effect(() => {
      const component = this.svc.component();
      const inputs = this.svc.inputs();

      this.outlet().clear();
      if (!component) return;

      const ref = this.outlet().createComponent(component);
      Object.entries(inputs).forEach(([key, value]) => {
        ref.setInput(key, value);
      });
      ref.changeDetectorRef.detectChanges();
    });
  }

  protected onRequestClose(event: Event): void {
    if (this.svc.preventClose()) {
      event.preventDefault();
      return;
    }
    this.svc.close();
  }
}
